import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

// Shared modal shell. Handles the portal, backdrop, ESC + backdrop-click
// dismissal, and the standard card styling. Composition is intentionally
// loose — callers structure their own header/body sections (separated by
// `border-t border-border` when the visual break helps) and use
// <ModalFooter> for the action row.

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  // xl is reserved for modals with multiple side-by-side panels (e.g.
  // the transactions filter modal's Account + Category columns).
  xl: 'max-w-4xl',
  // full leaves the card to fill the viewport's content width — the
  // outer wrapper still adds `p-4` so there's a visible margin on each
  // side. Use when even xl is too narrow (data-heavy multi-column
  // modals on wide displays).
  full: 'max-w-none',
};

interface ModalProps {
  onClose: () => void;
  size?: ModalSize;
  /** Extra classes on the inner card (rare — most modals don't need this). */
  className?: string;
  children: ReactNode;
}

export function Modal({ onClose, size = 'md', className, children }: ModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={clsx(
          'w-full rounded-2xl border border-border bg-elevated shadow-xl flex flex-col',
          SIZE_CLASS[size],
          className,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

interface ModalFooterProps {
  /**
   * `end` (default): standard Cancel/Save layout with both actions
   * right-aligned.
   * `between`: destructive action on the far left, dismissive action on the
   * far right — used when there's an asymmetric pair of choices and you
   * want them visually separated.
   */
  align?: 'end' | 'between';
  children: ReactNode;
}

export function ModalFooter({ align = 'end', children }: ModalFooterProps) {
  return (
    <div
      className={clsx(
        'border-t border-border p-4 flex items-center gap-2',
        align === 'end' ? 'justify-end' : 'justify-between',
      )}
    >
      {children}
    </div>
  );
}
